import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trophy, Lock, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUser } from '@core/contexts/ProfileContext';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { gameApi } from '@core/game/api';
import type { GameAchievementView } from '@lunit/shared-types';

const rarityStyles: Record<string, string> = {
  common: 'bg-gray-100 text-gray-700',
  rare: 'bg-primary/20 text-primary',
  epic: 'bg-primary/20 text-primary',
  legendary: 'bg-yellow-100 text-yellow-700'
};

export default function AchievementsPage() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { data: achievements = [], isLoading } = useQuery<GameAchievementView[]>({
    queryKey: ['achievements', user?.id],
    queryFn: () => gameApi.getAchievements(user!.id) as Promise<GameAchievementView[]>,
    enabled: Boolean(user?.id)
  });

  const claimMutation = useMutation({
    mutationFn: (achievementId: string) => gameApi.claimAchievement(user!.id, achievementId),
    onSuccess: () => {
      toast.success('Reward claimed!');
      void queryClient.invalidateQueries({ queryKey: ['achievements'] });
      void queryClient.invalidateQueries({ queryKey: ['gameProfile'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to claim reward');
    }
  });

  const grouped = useMemo(() => {
    return achievements.reduce<Record<string, GameAchievementView[]>>((acc, achievement) => {
      if (!acc[achievement.category]) {
        acc[achievement.category] = [];
      }
      acc[achievement.category].push(achievement);
      return acc;
    }, {});
  }, [achievements]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading achievements...</div>;
  }

  if (!user) {
    return <div className="flex h-screen items-center justify-center">Please login to view achievements</div>;
  }

  const unlockedCount = achievements.filter((achievement) => achievement.unlocked).length;
  const progress = achievements.length ? Math.floor((unlockedCount / achievements.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-page p-4 pb-16">
      <header className="mb-6">
        <h1 className="flex items-center text-3xl font-bold text-text-main">
          <Trophy className="mr-3 text-primary" /> Achievements
        </h1>
        <p className="mt-1 text-sm text-text-muted">Complete challenges to earn rewards!</p>
      </header>

      <Card className="mb-6">
        <div className="grid grid-cols-3 text-center">
          <div>
            <div className="text-2xl font-bold text-green-600">{unlockedCount}</div>
            <div className="text-sm text-gray-600">Unlocked</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-500">{achievements.length}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">{progress}%</div>
            <div className="text-sm text-gray-600">Complete</div>
          </div>
        </div>
      </Card>

{Object.entries(grouped).map(([category, items]) => (
        <section key={category} className="mb-6">
          <h2 className="mb-3 text-xl font-semibold capitalize text-text-main">
            {category.replace(/_/g, ' ')}
          </h2>
          <div className="space-y-3">
            {items.map((achievement) => (
              <Card key={achievement.id} className={`relative border border-border ${achievement.unlocked ? '' : 'opacity-75'}`}>
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-16 w-16 items-center justify-center rounded-2xl text-2xl ${
                      achievement.unlocked ? rarityStyles[achievement.rarity] ?? rarityStyles.common : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {achievement.unlocked ? achievement.icon : <Lock size={24} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-text-main">{achievement.name}</h3>
                      {achievement.unlocked && <Check className="text-green-500" size={18} />}
                    </div>
                    <p className="mt-1 text-sm text-text-muted">{achievement.description}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs">
                      {Number(achievement.rewardCoins) > 0 && (
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700">
                          +{achievement.rewardCoins} coins
                        </span>
                      )}
                      {achievement.rewardEnergy > 0 && (
                        <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-700">
                          +{achievement.rewardEnergy} energy
                        </span>
                      )}
                      {achievement.rewardXP > 0 && (
                        <span className="rounded-full bg-primary/20 px-3 py-1 text-primary">
                          +{achievement.rewardXP} XP
                        </span>
                      )}
                    </div>
                  </div>
                  {achievement.unlocked && !achievement.claimed && (
                    <Button
                      size="sm"
                      onClick={() => claimMutation.mutate(achievement.id)}
                      disabled={claimMutation.isPending}
                    >
                      Claim
                    </Button>
                  )}
                  {achievement.unlocked && achievement.claimed && (
                    <span className="text-sm font-semibold text-green-600">Claimed</span>
                  )}
                </div>
                <span
                  className={`absolute right-3 top-3 rounded-full px-3 py-1 text-xs font-semibold ${
                    rarityStyles[achievement.rarity] ?? rarityStyles.common
                  }`}
                >
                  {achievement.rarity}
                </span>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
